import { useState } from "react";
import {
	Combobox,
	ComboboxButton,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
	Field,
	Label,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import RequiredField from "../RequiredField";
import { SimpleOption } from "./SimpleListbox";
import { filterOptions } from "./optionFilter";

interface SearchableSelectProps {
	id: string;
	value: string | number | undefined;
	setValue: (value: string | number | undefined) => void;
	options: SimpleOption[];
	label?: string;
	requiredField?: boolean;
	prompt?: string;
	fieldClasses?: string;
	labelClasses?: string;
	inputClasses?: string;
}

/**
 * A dropdown you can type into, for lists long enough that scrolling to find an entry is a
 * chore. Typing filters the list, so the states can be reached either by name or by postal
 * code: "nc", "no" and "north car" all reach North Carolina.
 *
 * This is a Combobox rather than the Listbox behind {@link SimpleListbox} because here typing
 * is the point. Headless UI then owns which option is highlighted, keeping it in view and
 * handling the arrow keys and Enter, instead of that being reimplemented on top of a control
 * whose selection is driven by the mouse.
 */
export default function SearchableSelect(props: SearchableSelectProps) {
	const [query, setQuery] = useState("");

	const selectedOption = props.options.find((option) => option.value == props.value) ?? null;
	const visibleOptions = filterOptions(props.options, query);
	const isEmpty = props.options.length === 0;

	return (
		<Field className={props.fieldClasses}>
			{props.label && (
				<Label
					htmlFor={props.id}
					className={`${props.labelClasses ? `${props.labelClasses} ` : ""}block mb-2 text-sm font-medium text-gray-90`}>
					{props.label}
					{props.requiredField && <RequiredField />}
				</Label>
			)}
			<Combobox
				immediate
				disabled={isEmpty}
				value={selectedOption}
				onChange={(option: SimpleOption | null) => {
					props.setValue(option?.value);
					setQuery("");
				}}
				onClose={() => setQuery("")}>
				<div className="relative">
					<ComboboxInput
						id={props.id}
						autoComplete="off"
						placeholder={props.prompt}
						displayValue={(option: SimpleOption | null) => option?.label ?? ""}
						onChange={(e) => setQuery(e.target.value)}
						className={`${props.inputClasses ?? ""} relative w-full h-9 rounded-lg border border-gray-300 py-2 pl-3 pr-10 text-left text-sm text-gray-900 disabled:bg-slate-200/75 focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600`}
					/>
					<ComboboxButton
						aria-label={props.label ? `Show ${props.label} options` : "Show options"}
						className="absolute inset-y-0 right-0 flex items-center px-2">
						<ChevronDownIcon className="h-5 w-5 text-gray-400" />
					</ComboboxButton>

					<ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm empty:invisible">
						{visibleOptions.length === 0 && (
							<div className="cursor-default select-none py-2 pl-3 pr-10 text-gray-400">
								No match
							</div>
						)}
						{visibleOptions.map((option) => (
							<ComboboxOption
								key={option.value}
								value={option}
								disabled={option.disabled ?? false}
								className="relative cursor-pointer select-none py-2 pl-3 pr-10 text-gray-900 data-[focus]:bg-primary-100 data-[focus]:text-primary-900 data-[disabled]:bg-slate-200/75 data-[disabled]:cursor-default data-[disabled]:text-gray-400">
								{({ selected }) => (
									<span
										className={`flex items-center ${selected ? "font-medium" : "font-normal"}`}>
										{option.color && (
											<span
												className={`inline-block w-3 h-3 rounded-full mr-2 ${option.color}`}
											/>
										)}
										{option.label}
									</span>
								)}
							</ComboboxOption>
						))}
					</ComboboxOptions>
				</div>
			</Combobox>
		</Field>
	);
}
